import React from 'react'
import { useFormContext } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'

// Helper to get nested error from dot-notation name like "location.address"
const getNestedError = (errors, name) => {
  const parts = name.split('.')
  let current = errors
  for (const part of parts) {
    if (!current || !current[part]) return undefined
    current = current[part]
  }
  return current?.message
}

// Helper to check if a field has been touched and is valid
const getFieldState = (name, formState) => {
  const error = getNestedError(formState.errors, name)
  const parts = name.split('.')
  let touchedCheck = formState.touchedFields
  for (const part of parts) {
    if (!touchedCheck || !touchedCheck[part]) return { error, touched: false }
    touchedCheck = touchedCheck[part]
  }
  return { error, touched: true }
}

const FormField = ({
  label,
  id,
  name,
  type = 'text',
  required = false,
  helpText,
  className = '',
  icon: Icon,
  suffix,
  children,
  ...props
}) => {
  const formContext = useFormContext()

  if (!formContext) {
    console.error('FormField must be used within a FormProvider')
    return null
  }

  const {
    register,
    formState
  } = formContext

  const { error, touched } = getFieldState(name, formState)
  const isValid = touched && !error && formState.dirtyFields[name]

  const baseClasses = `block w-full rounded-xl text-base bg-bg text-text
    placeholder:text-text-muted/60
    transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-secondary`

  const borderClasses = error
    ? 'border-2 border-red-400 dark:border-red-400 focus:border-red-500 focus:ring-3 focus:ring-red-500/15 dark:focus:ring-red-500/20'
    : isValid
      ? 'border-2 border-green-400 dark:border-green-400 focus:border-green-500 focus:ring-3 focus:ring-green-500/15'
      : 'border border-border hover:border-border-strong focus:border-primary-500 focus:ring-3 focus:ring-primary-500/15 dark:focus:ring-primary-500/20 focus:border-2'

  const sizeClasses = type === 'textarea'
    ? 'px-4 py-3.5'
    : 'min-h-[52px] px-4'

  const iconPadding = Icon ? 'pl-12' : ''
  const suffixPadding = suffix ? 'pr-16' : ''

  const inputClasses = `${baseClasses} ${borderClasses} ${sizeClasses} ${iconPadding} ${suffixPadding}`

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <div className="flex items-baseline justify-between">
          <label
            htmlFor={id}
            className={`block text-sm font-semibold transition-colors duration-200 ${
              error ? 'text-red-600 dark:text-red-400' : 'text-text'
            }`}
          >
            {label}
            {required && (
              <span className="text-red-500 dark:text-red-400 ml-0.5" aria-hidden="true">*</span>
            )}
          </label>
          {type === 'textarea' && props.maxLength && (
            <span className="text-xs text-text-muted tabular-nums">
              {formContext.watch(name)?.length || 0}/{props.maxLength}
            </span>
          )}
        </div>
      )}
      <div className="relative group">
        {Icon && (
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <Icon
              className={`h-5 w-5 transition-colors duration-200 ${
                error
                  ? 'text-red-400'
                  : 'text-text-muted group-focus-within:text-primary-500'
              }`}
            />
          </div>
        )}
        {type === 'textarea' ? (
          <textarea
            id={id}
            className={`${inputClasses} resize-none`}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
            {...register(name)}
            {...props}
          />
        ) : type === 'select' ? (
          <select
            id={id}
            className={`${inputClasses} appearance-none cursor-pointer`}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
            {...register(name)}
            {...props}
          >
            {children}
          </select>
        ) : (
          <input
            type={type}
            id={id}
            className={inputClasses}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
            {...register(name)}
            {...props}
          />
        )}
        {/* Suffix text */}
        {suffix && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
            <span className="text-sm font-medium text-text-muted">{suffix}</span>
          </div>
        )}
        {/* Status indicator icon */}
        {!suffix && (error || isValid) && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5">
            <AnimatePresence mode="wait">
              {error ? (
                <motion.div
                  key="error-icon"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                >
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                </motion.div>
              ) : isValid ? (
                <motion.div
                  key="valid-icon"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.15 }}
                >
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        )}
      </div>
      {/* Help text */}
      <AnimatePresence mode="wait">
        {error ? (
          <motion.p
            key="error"
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -4, height: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 font-medium"
            id={`${id}-error`}
            role="alert"
          >
            {error}
          </motion.p>
        ) : helpText ? (
          <motion.p
            key="help"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="text-sm text-text-muted leading-relaxed"
            id={`${id}-help`}
          >
            {helpText}
          </motion.p>
        ) : null}
      </AnimatePresence>
    </div>
  )
}

export default FormField
